import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Clock, MapPin } from "lucide-react";
import { SITE } from "../config/siteConfig";

const EASE = [0.16, 1, 0.3, 1];

const parent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};
const line = {
  hidden: { y: "112%" },
  show: { y: "0%", transition: { duration: 0.75, ease: EASE } },
};
const fade = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

export default function Hero({ start }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const houseY = useTransform(scrollYProgress, [0, 1], [0, 44]);
  const ballY = useTransform(scrollYProgress, [0, 1], [0, -64]);
  const state = start ? "show" : "hidden";

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="hero">
      <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-6 px-5 pb-14 pt-6 md:px-10 md:pb-20 md:pt-10 lg:grid-cols-12 lg:gap-6 lg:pt-14">
        <motion.div
          variants={parent}
          initial="hidden"
          animate={state}
          className="relative z-10 lg:col-span-7"
        >
          <motion.p variants={fade} className="eyebrow text-brand-dark">
            <span className="eyebrow-rule" aria-hidden="true" />
            {SITE.tagline}
          </motion.p>

          <motion.div variants={fade} className="mt-6 sm:mt-8">
            <motion.img
              src="/images/Hitstube_Schriftzug.webp?v=2"
              alt="Schriftzug der Hitsstube: OLMA AFTERPARTY – HITS STUBE"
              width={1714}
              height={885}
              fetchPriority="high"
              className="w-full max-w-[420px] lg:max-w-[480px]"
              initial={reduce ? undefined : { clipPath: "inset(0 100% 0 0)" }}
              animate={
                reduce
                  ? undefined
                  : state === "show"
                    ? { clipPath: "inset(0 0% 0 0)" }
                    : { clipPath: "inset(0 100% 0 0)" }
              }
              transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
            />
          </motion.div>

          <h1
            data-testid="hero-heading"
            className="mt-7 font-display text-[clamp(2.4rem,5.4vw,4.6rem)] leading-[0.98] tracking-tight"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" variants={line}>
                {SITE.claim[0]}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block" variants={line}>
                ist vor der <span className="text-brand-red">Hitsstube.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={fade}
            className="mt-6 max-w-[54ch] text-base leading-relaxed md:text-lg"
          >
            {SITE.heroIntro}
          </motion.p>

          <motion.p
            variants={fade}
            data-testid="hero-info-line"
            className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[15px] font-semibold md:text-base"
          >
            <Clock className="h-4 w-4 shrink-0 text-brand-red" aria-hidden="true" />
            <span>
              {SITE.infoLine.days} · {SITE.infoLine.time}
            </span>
            <span aria-hidden="true" className="text-brand-red">
              ·
            </span>
            <MapPin className="h-4 w-4 shrink-0 text-brand-red" aria-hidden="true" />
            <span>{SITE.infoLine.location}</span>
          </motion.p>

          <motion.div variants={fade} className="mt-8 flex flex-wrap gap-3 sm:gap-4">
            <a data-testid="hero-cta-programm" href="#programm" className="btn-primary">
              Programm ansehen
            </a>
            <a
              data-testid="hero-cta-reservation"
              href={SITE.reservationMailto}
              className="btn-secondary"
            >
              Tisch anfragen
            </a>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={reduce ? undefined : { y: ballY }}
            className="absolute -top-1 right-0 sm:-top-2 lg:top-0"
          >
            <motion.div
              initial={reduce ? undefined : { opacity: 0, scale: 0.8 }}
              animate={start || reduce ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, ease: EASE, delay: 0.45 }}
            >
              <motion.div
                animate={reduce ? undefined : { rotate: [-7, 7, -7] }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                style={{ transformOrigin: "50% -30%" }}
              >
                <img
                  src="/images/Hitstube_Discokugel.webp?v=2"
                  alt=""
                  aria-hidden="true"
                  draggable="false"
                  width={1361}
                  height={1155}
                  fetchPriority="high"
                  className="w-20 sm:w-24 lg:w-32"
                />
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div style={reduce ? undefined : { y: houseY }} className="mt-8 sm:mt-10 lg:mt-28">
            <motion.div
              initial={reduce ? undefined : { opacity: 0, y: 26 }}
              animate={start || reduce ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease: EASE, delay: 0.25 }}
            >
              <motion.div
                animate={reduce ? undefined : { y: [0, -7, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              >
                <img
                  src="/images/Hitstube_Haus.webp?v=2"
                  alt="Rotes Waaghaus am Marktplatz St. Gallen"
                  width={1264}
                  height={1029}
                  fetchPriority="high"
                  className="mx-auto w-full max-w-[400px] lg:max-w-[520px]"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
